import { useCallback, useEffect, useState } from "react";
import React from "react";
import { Link } from "react-router-dom";
import { Dropdown, Menu, Button, Badge } from "antd";
import { EllipsisOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import DateRangePopup from "../../Utils/dateFilterPopup";
import defaultImage from "../../Global Icons/default_image.png";
import { viewCategoryList } from "../../Redux/Actions/categoryActions";
import TableComponent from "../../Global Functions/tableComponents";

const CategoriesMain = () => {
  const dispatch = useDispatch();

  // const STALE_TIME = process.env.REACT_APP_STALE_TIME;

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isDatePopupVisible, setIsDatePopupVisible] = useState(false);
  const [dateRange, setDateRange] = useState({ start: null, end: null });

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    pageSizeOptions: ["5", "10", "20", "50", "100"],
  });

  const { categories, loading } = useSelector((state) => state.categories);

  const { admin } = useSelector((state) => state.admin || {});
  const accessCode = admin?.data?.access_code || [];
  const canAdd = Array.isArray(accessCode) && accessCode.includes('add');
  const canEdit = Array.isArray(accessCode) && accessCode.includes('edit');

  const fetchDetails = useCallback(async (page, pageSize) => {
    const categoryData = {
      page,
      limit: pageSize,
      filter: debouncedSearch,
      ...(dateRange.start && { start_date: dateRange.start.format("YYYY-MM-DD") }),
      ...(dateRange.end && { end_date: dateRange.end.format("YYYY-MM-DD") }),
    };
    try {
      await dispatch(viewCategoryList(categoryData));
    } catch {
      toast.error("Failed to fetch projects");
    }
  },
    [dispatch, debouncedSearch, dateRange]
  );

  useEffect(() => {
    fetchDetails(pagination.current, pagination.pageSize);
  }, [pagination.current, pagination.pageSize, debouncedSearch, dateRange, fetchDetails]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);


  useEffect(() => {
    if (categories?.total) {
      setPagination((prevData) => ({ ...prevData, total: categories.total }));
    }
  }, [categories]);

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };


  const handleDateRangeApply = (start, end) => {
    setDateRange(start === null && end === null ? { start: null, end: null } : { start, end });
    setIsDatePopupVisible(false);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  useEffect(() => {
    setPagination((prev) => ({ ...prev, current: 1 }));
  }, [debouncedSearch]);

  const getDropDownMenu = (record) => (
    <Menu>
      <Menu.Item key="view">
        <Link to={`/categories/view/${record._id}?name=${encodeURIComponent(record.name)}`}>
          View
        </Link>
      </Menu.Item>
      {
        canEdit && (
          <Menu.Item key="edit">
            <Link to={`/categories/edit/${record._id}`}>Edit</Link>
          </Menu.Item>
        )
      }
    </Menu >
  );

  const columns = [
    {
      title: "Sr No.",
      align: "center",
      render: (_, __, index) => index + 1,
    },
    {
      title: "Category Name",
      dataIndex: "name",
      align: "center",
      key: "name",
    },
    {
      title: "Business Type",
      dataIndex: "business_type",
      key: "business_type",
      align: "center",
      render: (business_type) => {
        if (!business_type || (Array.isArray(business_type) && business_type.length === 0)) {
          return "N/A";
        }
        const types = Array.isArray(business_type) ? business_type : [business_type];
        return (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, justifyContent: "center" }}>
            {types.map((type) => (
              <span
                key={type}
                style={{
                  padding: "2px 10px",
                  borderRadius: 999,
                  fontSize: 12,
                  fontWeight: 500,
                  textTransform: "capitalize",
                  background:
                    type === "fresh" ? "#ecfdf5" :
                      type === "resale" ? "#eff6ff" :
                        type === "business" ? "#fefce8" :
                          type === "rental" ? "#fdf4ff" : "#f3f4f6",
                  color:
                    type === "fresh" ? "#059669" :
                      type === "resale" ? "#2563eb" :
                        type === "business" ? "#ca8a04" :
                          type === "rental" ? "#9333ea" : "#6b7280",
                  border: `1px solid ${type === "fresh" ? "#a7f3d0" :
                    type === "resale" ? "#bfdbfe" :
                      type === "business" ? "#fde68a" :
                        type === "rental" ? "#e9d5ff" : "#e5e7eb"
                    }`,
                }}
              >
                {type}
              </span>
            ))}
          </div>
        );
      },
    },
    {
      title: "Image",
      dataIndex: "image",
      key: "imageUrl",
      align: "center",
      render: (image, record) => (
        <img
          key={record._id}
          src={image || defaultImage}
          alt="No Data"
          decoding="async"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = defaultImage;
          }}
          style={{ borderRadius: "20px", height: "50px", width: "50px" }}
        />
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      align: "center",
      render: (status) => (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            padding: "4px 8px",
            border: "1px solid #d9d9d9", // Light gray border
            borderRadius: "10px", // Rounded edges
          }}
        >
          <Badge
            color={status === "active" ? "green" : "red"}
            text={status.charAt(0).toUpperCase() + status.slice(1) || ""}
            background
          />
        </span>
      ),
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      render: (_, record) => (
        <Dropdown overlay={getDropDownMenu(record)} trigger={["click"]}>
          <Button shape="square" icon={<EllipsisOutlined />} />
        </Dropdown>
      ),
    },
  ];

  return (
    <>
      <TableComponent
        h3heading="Categories Resale List"
        buttonhead2={canAdd ? "Add Category" : null}
        navigateTo2={canAdd ? "/categories/add" : null}
        setState={setSearchQuery}
        columns={columns}
        dataSource={Array.isArray(categories?.data) ? categories.data : []}
        rowKey="_id"
        loading={loading}
        pagination={pagination}
        onCalendarClick={() => setIsDatePopupVisible(true)}
        onChange={handleTableChange}
      ></TableComponent>

      <DateRangePopup
        visible={isDatePopupVisible}
        initialStart={dateRange.start}
        initialEnd={dateRange.end}
        onCancel={() => setIsDatePopupVisible(false)}
        onOk={handleDateRangeApply}
      />
    </>
  );
};

export default CategoriesMain;
